import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { animate, AnimatePresence, motion } from 'motion/react';
import { abrir, aparecer, CURVA, resorte, suave } from './movimiento';
import type { Orden } from '../api';
import type { Vista } from './Layout';
import { aviso, botonSecundario, c, campo, chip, chipBoton, miles, punto, tarjeta, tono, tonoDeEstado, type Tono } from '../theme';

/** Ícono de Material Symbols Rounded. */
export function Icono({
  nombre,
  size = 20,
  color,
  style,
}: {
  nombre: string;
  size?: number;
  color?: string;
  style?: CSSProperties;
}) {
  return (
    <span className="ms" aria-hidden="true" style={{ fontSize: size, color, flex: `0 0 ${size}px`, ...style }}>
      {nombre}
    </span>
  );
}

/**
 * Qué pidió una orden de trabajo. Con un artículo es su detalle, en una línea;
 * con varios, cuántos son y el total, y debajo un renglón por artículo: la
 * orden es una sola, pero lo que se arma es cada uno de sus artículos.
 */
export function DetalleOrden({ orden }: { orden: Pick<Orden, 'detalle' | 'lineas'> }) {
  if (orden.lineas.length <= 1) return <span style={{ fontSize: 13, color: c.texto2 }}>{orden.detalle}</span>;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 13, color: c.texto, fontWeight: 500 }}>{orden.detalle}</span>
      {orden.lineas.map((l) => (
        <span key={l.articulo} style={{ display: 'flex', gap: 6, fontSize: 12.5, color: c.texto2 }}>
          <span aria-hidden="true" style={{ color: c.texto4 }}>
            ·
          </span>
          {l.detalle}
        </span>
      ))}
    </span>
  );
}

/**
 * Chip de estado con su punto de color, como en el resto del sistema. Cuando
 * el estado cambia (Pedido → Aceptado → Entregado) el color pasa de uno al
 * otro y el ancho se acomoda, en vez de saltar.
 */
export function EstadoChip({ estado, tono: forzado }: { estado: string; tono?: Tono }) {
  const t = forzado ?? tonoDeEstado(estado);
  const colores = tono(t);
  return (
    // Sin `layout`: con cientos de chips en una tabla que se filtra, cada uno
    // se iría deslizando por su cuenta detrás de su fila.
    <motion.span
      initial={false}
      animate={{ backgroundColor: colores.bg, borderColor: colores.bd, color: colores.fg }}
      transition={suave}
      style={chip(t)}
    >
      <motion.span initial={false} animate={{ backgroundColor: colores.dot }} style={punto(t)} />
      <motion.span key={estado} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={suave}>
        {estado}
      </motion.span>
    </motion.span>
  );
}

/**
 * Un número que cuenta hasta su valor: al aparecer arranca de cero y, cuando
 * cambia, recorre la diferencia. Así un total que sube se ve subir.
 */
export function Numero({
  valor,
  formato = miles,
  desdeCero = true,
}: {
  valor: number;
  formato?: (n: number) => string;
  /**
   * `false` para un número dentro de una frase («Vaciar 3 posiciones»): al
   * aparecer ya muestra su valor, y sólo recorre la diferencia cuando cambia.
   * Si no, durante el primer cuadro diría «Vaciar 0 posición».
   */
  desdeCero?: boolean;
}) {
  const inicio = desdeCero ? 0 : valor;
  const [mostrado, setMostrado] = useState(inicio);
  const desde = useRef(inicio);
  useEffect(() => {
    const control = animate(desde.current, valor, {
      duration: 0.8,
      ease: CURVA,
      onUpdate: (v) => {
        desde.current = v;
        setMostrado(v);
      },
    });
    return () => control.stop();
  }, [valor]);
  const decimales = Number.isInteger(valor) ? 0 : 1;
  const redondo = Number(mostrado.toFixed(decimales));
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formato(redondo)}</span>;
}

/**
 * Un texto que cambia en el lugar («Guardar» → «Guardando…», «Confirmar» /
 * «No»): el viejo sale hacia arriba y el nuevo entra desde abajo, en vez de
 * pisarse de golpe. `clave` decide cuándo es «otro» texto; si no viene, es
 * el texto mismo.
 */
export function Cambia({ clave, children }: { clave?: string; children: ReactNode }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={clave ?? (typeof children === 'string' ? children : '')}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.16, ease: CURVA }}
        style={{ display: 'inline-block' }}
      >
        {children}
      </motion.span>
    </AnimatePresence>
  );
}

/**
 * Un chip o pestaña que se elige de una fila (filtros, pestañas). El resaltado
 * no aparece y desaparece: viaja del que estaba elegido al nuevo. `grupo`
 * identifica la fila; tiene que ser distinto en cada fila de la misma pantalla.
 */
export function ChipBoton({
  grupo,
  activo,
  children,
  style,
  ...props
}: { grupo: string; activo: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      {...props}
      style={{
        ...chipBoton(false),
        position: 'relative',
        isolation: 'isolate',
        color: activo ? c.verde : c.texto2,
        ...style,
      }}
    >
      {activo && <PildoraActiva grupo={grupo} />}
      {children}
    </button>
  );
}

/**
 * El chip que tenía el resaltado en cada fila. Se guarda el elemento y no su
 * posición: medida al montarse quedaba vieja (la fila se acomoda cuando llegan
 * los datos) y el resaltado arrancaba de un lugar donde no había nada.
 */
const ultimoChip: Record<string, HTMLElement> = {};

/** Dónde está un elemento dentro de su fila, sin importar el scroll. */
export const medirEnFila = (el: HTMLElement) => ({ x: el.offsetLeft, y: el.offsetTop, ancho: el.offsetWidth });

/**
 * El resaltado del chip elegido: nace en el chip nuevo corrido a donde estaba
 * el anterior (y con su ancho) y se desliza hasta su lugar.
 *
 * No es un `layoutId`: Motion lo mide contra la página, y al cambiar de
 * pantalla con la página scrolleada el resaltado volaba desde la mitad de la
 * pantalla. `offsetLeft`/`offsetTop` se miden dentro de la fila: ni el scroll
 * de la página ni el de una `.wms-tira` los mueven.
 */
function PildoraActiva({ grupo }: { grupo: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const chip = el?.parentElement;
    if (!el || !chip) return;
    const ahora = medirEnFila(chip);
    // El chip anterior se mide ahora, en la fila como está. Si ya no está en la
    // página (se cambió de pantalla) no hay de dónde venir: aparece en su lugar.
    // El «desde» queda en el elemento: React (en desarrollo) corre el efecto
    // dos veces y la segunda ya encontraría al chip nuevo como el anterior.
    const previo = ultimoChip[grupo];
    const guardado = el.dataset.desde;
    const antes: typeof ahora | undefined = guardado
      ? JSON.parse(guardado)
      : previo && previo !== chip && previo.isConnected
        ? medirEnFila(previo)
        : undefined;
    el.dataset.desde = JSON.stringify(antes ?? ahora);
    ultimoChip[grupo] = chip;
    if (!antes || (antes.x === ahora.x && antes.y === ahora.y)) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const control = animate(
      el,
      { x: [antes.x - ahora.x, 0], y: [antes.y - ahora.y, 0], width: [antes.ancho + 2, ahora.ancho + 2] },
      resorte,
    );
    // Terminado, el ancho vuelve a salir del chip: si su texto cambia (un contador), lo acompaña.
    void control.then(() => {
      el.style.width = '';
    });
    return () => control.stop();
  }, [grupo]);
  return (
    <span
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: -1,
        borderRadius: 999,
        background: c.verdeSuave,
        border: `1px solid ${c.verde}`,
        zIndex: -1,
      }}
    />
  );
}

export function Cargando({ texto = 'Cargando…', alto = 160 }: { texto?: string; alto?: number }) {
  return (
    <div
      className="wms-cargando"
      role="status"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        minHeight: alto,
        color: c.texto3,
        fontSize: 13.5,
      }}
    >
      <Icono nombre="progress_activity" size={26} />
      {texto}
    </div>
  );
}

export function PanelError({ mensaje, onReintentar }: { mensaje: string; onReintentar?: () => void }) {
  return (
    <motion.div {...aparecer} style={{ ...aviso('err'), display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }} role="alert">
      <Icono nombre="error" size={20} color={c.rojo} />
      <span style={{ flex: 1, minWidth: 0 }}>{mensaje}</span>
      {onReintentar && (
        <button
          type="button"
          onClick={onReintentar}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            border: `1px solid ${c.bordeFuerte}`,
            background: c.panel,
            color: c.texto,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reintentar
        </button>
      )}
    </motion.div>
  );
}

export function Vacio({
  icono,
  titulo,
  detalle,
  accion,
}: {
  icono: string;
  titulo: string;
  detalle: string;
  accion?: ReactNode;
}) {
  return (
    <motion.div {...aparecer} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '48px 20px' }}>
      <span
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: c.fondo,
          border: `1px solid ${c.borde}`,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Icono nombre={icono} size={26} color={c.texto3} />
      </span>
      <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>{titulo}</span>
      <span style={{ fontSize: 13, color: c.texto2, textAlign: 'center', textWrap: 'pretty' }}>{detalle}</span>
      {accion && <div style={{ marginTop: 8 }}>{accion}</div>}
    </motion.div>
  );
}

/** Tarjeta de indicador del dashboard. */
export function Kpi({
  rotulo: texto,
  valor,
  pie,
  color,
  pieColor,
}: {
  rotulo: string;
  valor: ReactNode;
  pie: string;
  color?: string;
  pieColor?: string;
}) {
  return (
    <div style={{ ...tarjeta, padding: '18px 20px' }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: c.texto3,
        }}
      >
        {texto}
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: 34,
          fontWeight: 600,
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums',
          color: color ?? c.texto,
        }}
      >
        {typeof valor === 'number' ? <Numero valor={valor} /> : valor}
      </div>
      <div style={{ marginTop: 4, fontSize: 12.5, color: pieColor ?? c.texto2 }}>{pie}</div>
    </div>
  );
}

/** Cabecera de vista: título grande, bajada y acciones a la derecha. */
export function Encabezado({
  titulo: texto,
  bajada,
  tamano = 27,
  children,
}: {
  /**
   * Sólo cuando dice algo que la barra de arriba no dice (p. ej. la fecha del
   * resumen): el nombre de la pantalla ya está ahí, repetirlo en grande abajo
   * era ruido.
   */
  titulo?: ReactNode;
  bajada: string;
  tamano?: number;
  children?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 'min(260px, 100%)' }}>
        {texto && (
          <h2 style={{ margin: '0 0 6px', fontSize: tamano, fontWeight: 600, letterSpacing: '-0.035em', color: c.texto }}>
            {texto}
          </h2>
        )}
        <div style={{ fontSize: 14, color: c.texto2, textWrap: 'pretty' }}>{bajada}</div>
      </div>
      {children && <div className="acciones-encabezado">{children}</div>}
    </div>
  );
}

/**
 * La marca del sistema: el logo de RDM × ZF con el nombre al lado. Se usa en
 * la barra lateral y en el ingreso, así que vive acá y no duplicado en cada
 * pantalla.
 */
export function Marca({ tamano = 32 }: { tamano?: number }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
      <img
        src="/logo.png"
        alt="RDM Logística · ZF"
        width={tamano}
        height={Math.round((tamano * 254) / 320)}
        style={{ display: 'block', flex: `0 0 ${tamano}px` }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
        <span style={{ fontSize: tamano > 36 ? 15 : 13.5, fontWeight: 600, color: c.texto }}>RDM Logística</span>
        <span style={{ fontSize: tamano > 36 ? 12 : 11, color: c.texto3 }}>WMS · Depósito ZF</span>
      </span>
    </span>
  );
}

/* --------------------------------------------------------------- listas */

/**
 * Cuántas filas se muestran de una. Veinticinco es lo que entra en una
 * pantalla del depósito sin que haya que scrollear para llegar al pie.
 */
export const POR_PAGINA = 25;

/**
 * Parte una lista en páginas.
 *
 * Los datos ya vienen enteros del servidor: esto no es para no traerlos, es
 * para no dibujarlos. Con dos mil pallets, el navegador de la tablet del
 * depósito se arrastra pintando filas que nadie va a mirar.
 *
 * La página se acomoda sola cuando la lista se achica —al buscar, al
 * filtrar—: quedarse en la página 9 de una lista que ahora tiene dos deja la
 * pantalla en blanco y parece que la búsqueda no encontró nada.
 */
export function usePaginado<T>(items: T[], porPagina = POR_PAGINA) {
  const [pedida, setPagina] = useState(1);

  const paginas = Math.max(1, Math.ceil(items.length / porPagina));
  const pagina = Math.min(Math.max(1, pedida), paginas);
  const desde = (pagina - 1) * porPagina;

  return {
    visibles: items.slice(desde, desde + porPagina),
    pagina,
    paginas,
    total: items.length,
    desde,
    setPagina,
  };
}

/**
 * El pie de una lista paginada. No se dibuja si todo entra en una página:
 * «1 de 1» es ruido.
 */
export function Paginador({
  pagina,
  paginas,
  total,
  desde,
  que,
  onIr,
  porPagina = POR_PAGINA,
}: {
  pagina: number;
  paginas: number;
  total: number;
  desde: number;
  /** Qué se está contando, en plural: «pallets», «artículos». */
  que: string;
  onIr: (pagina: number) => void;
  porPagina?: number;
}) {
  if (paginas <= 1) return null;

  const hasta = Math.min(desde + porPagina, total);
  const boton = (texto: string, icono: string, destino: number, apagado: boolean, alDerecho = false) => (
    <button
      type="button"
      onClick={() => onIr(destino)}
      disabled={apagado}
      style={{
        ...botonSecundario,
        padding: '8px 14px',
        fontSize: 13,
        opacity: apagado ? 0.45 : 1,
        cursor: apagado ? 'default' : 'pointer',
        flexDirection: alDerecho ? 'row-reverse' : 'row',
      }}
    >
      <Icono nombre={icono} size={18} color={c.texto3} />
      {texto}
    </button>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        flexWrap: 'wrap',
        borderTop: `1px solid ${c.borde}`,
      }}
    >
      {/* En el celular el conteo va en su renglón y abajo los dos botones a
          los costados con la página en el medio: si no, «Siguiente» quedaba
          solo en un renglón aparte. */}
      <span className="basis-full tablet:basis-auto tablet:flex-1" style={{ fontSize: 12.5, color: c.texto3 }}>
        {desde + 1}–{hasta} de {total} {que}
      </span>
      {boton('Anterior', 'chevron_left', pagina - 1, pagina === 1)}
      <span
        className="flex-1 justify-center tablet:flex-none"
        style={{ fontSize: 13, color: c.texto2, fontVariantNumeric: 'tabular-nums', display: 'inline-flex', overflow: 'hidden' }}
      >
        {/* El número de página pasa como un cuentakilómetros. */}
        <motion.span key={pagina} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={suave}>
          {pagina}
        </motion.span>
        &nbsp;/ {paginas}
      </span>
      {boton('Siguiente', 'chevron_right', pagina + 1, pagina === paginas, true)}
    </div>
  );
}

/**
 * El buscador de una lista. Es el mismo de todas las pantallas: si en una se
 * busca distinto que en otra, el depósito tiene que aprender dos.
 */
export function Buscador({
  valor,
  onCambiar,
  placeholder,
  ancho = 420,
}: {
  valor: string;
  onCambiar: (v: string) => void;
  /** Por qué campos se puede buscar, que es lo que nadie adivina. */
  placeholder: string;
  ancho?: number;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 14px',
        height: 40,
        // En táctil el campo de adentro mide 44 (global.css): la caja crece con él.
        minHeight: 'var(--toque, auto)',
        background: c.panel,
        border: `1px solid ${c.bordeFuerte}`,
        borderRadius: 999,
        // Nunca más ancho que la pantalla: en 360 px los 240 fijos la desbordaban.
        minWidth: 'min(240px, 100%)',
        // 'auto' y no 0: dentro de una columna, flex: 1 le daba alto 0 y el buscador
        // quedaba aplastado en 20 px. Con base 'auto' respeta su alto de 40.
        flex: '1 1 auto',
        maxWidth: ancho,
      }}
    >
      <Icono nombre="search" size={19} color={c.texto3} />
      <input
        type="search"
        value={valor}
        onChange={(e) => onCambiar(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', fontSize: 13.5, color: c.texto }}
      />
    </label>
  );
}

/**
 * Filtra por lo que se escribió, mirando todo lo que la fila muestra.
 *
 * `campos` devuelve los textos de la fila: se busca sobre lo que se ve, que es
 * lo que la gente escribe en el buscador. Sin acentos y sin mayúsculas, porque
 * «Perfil» y «perfil» son la misma búsqueda y nadie va a escribir los tildes.
 */
export function filtrar<T>(items: T[], texto: string, campos: (item: T) => (string | number | null | undefined)[]): T[] {
  const q = normalizar(texto);
  if (!q) return items;
  return items.filter((item) => normalizar(campos(item).join(' ')).includes(q));
}

const normalizar = (t: string) =>
  t
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/** Lo que se pregunta antes de algo que no tiene vuelta atrás. */
export type Pregunta = {
  titulo: string;
  /** Qué se pierde o qué cambia, con números si los hay: «15 racks y 450 posiciones». */
  detalle?: ReactNode;
  /** El botón dice la consecuencia —«Borrar el sector y sus 15 racks»—, nunca «Aceptar». */
  boton: string;
  /**
   * Lo que hay que tipear para habilitar el botón, para lo que arrastra mucho
   * (un contenedor con sus pallets): un clic de más se da sin leer; tipear, no.
   */
  tipear?: string;
  /**
   * Rótulo de un campo de texto obligatorio (3 letras o más), para lo que
   * tiene que quedar explicado: con él, `confirmar` devuelve lo escrito, o
   * `null` si se canceló.
   */
  motivo?: string;
};

type Confirmar = {
  (p: Pregunta & { motivo: string }): Promise<string | null>;
  (p: Pregunta): Promise<boolean>;
};

/**
 * La confirmación de todo el sistema, para que borrar pregunte igual en cada
 * pantalla.
 *
 *     const [confirmar, dialogo] = useConfirmar();
 *     if (!(await confirmar({ titulo: '¿Borrar el sector B?', boton: 'Borrar el sector' }))) return;
 *     …
 *     return <>{…}{dialogo}</>;
 *
 * Es un `<dialog>` nativo: Esc cancela y el foco queda adentro sin código extra.
 */
export function useConfirmar(): [Confirmar, ReactNode] {
  const [abierta, setAbierta] = useState<(Pregunta & { responder: (r: boolean | string | null) => void }) | null>(
    null,
  );
  const confirmar = useCallback(
    (p: Pregunta) => new Promise<boolean | string | null>((responder) => setAbierta({ ...p, responder })),
    [],
  ) as Confirmar;
  const cerrar = (ok: boolean, texto = '') => {
    abierta?.responder(abierta.motivo ? (ok ? texto.trim() : null) : ok);
    setAbierta(null);
  };
  return [confirmar, abierta && <Dialogo pregunta={abierta} onCerrar={cerrar} />];
}

function Dialogo({ pregunta, onCerrar }: { pregunta: Pregunta; onCerrar: (ok: boolean, texto?: string) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [tipeado, setTipeado] = useState('');
  const [motivo, setMotivo] = useState('');
  useEffect(() => ref.current?.showModal(), []);
  const listo =
    (!pregunta.tipear || tipeado.trim().toUpperCase() === pregunta.tipear.toUpperCase()) &&
    (!pregunta.motivo || motivo.trim().length >= 3);
  const alto = { minHeight: 44, padding: '0 18px' };

  return (
    // El diálogo se abre desde el centro hacia los costados, y lo de adentro
    // aparece apenas después: primero la caja, después lo que pregunta.
    <motion.dialog
      ref={ref}
      {...abrir(18)}
      onCancel={(e) => {
        e.preventDefault();
        onCerrar(false);
      }}
      onClick={(e) => e.stopPropagation()}
      style={{ ...tarjeta, padding: 20, width: 'min(440px, calc(100vw - 32px))', border: `1px solid ${c.borde}`, color: c.texto }}
    >
      <motion.form
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...suave, delay: 0.14 }}
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (listo) onCerrar(true, motivo);
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Icono nombre="warning" size={22} color={c.rojo} />
          <strong style={{ fontSize: 16 }}>{pregunta.titulo}</strong>
        </div>
        {pregunta.detalle && <div style={{ fontSize: 13.5, color: c.texto2, lineHeight: 1.5 }}>{pregunta.detalle}</div>}
        {pregunta.tipear && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: c.texto2 }}>
            <span>
              Para confirmar, escribí <strong style={{ color: c.texto }}>{pregunta.tipear}</strong>
            </span>
            <input autoFocus value={tipeado} onChange={(e) => setTipeado(e.target.value)} style={campo} />
          </label>
        )}
        {pregunta.motivo && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: c.texto2 }}>
            <span>{pregunta.motivo}</span>
            <input
              autoFocus={!pregunta.tipear}
              required
              minLength={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              style={campo}
            />
          </label>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" autoFocus={!pregunta.tipear && !pregunta.motivo} onClick={() => onCerrar(false)} style={{ ...botonSecundario, ...alto }}>
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!listo}
            style={{
              ...botonSecundario,
              ...alto,
              border: `1px solid ${c.rojo}`,
              background: listo ? c.rojo : c.rojoSuave,
              color: listo ? '#fff' : c.rojo,
              cursor: listo ? 'pointer' : 'not-allowed',
            }}
          >
            {pregunta.boton}
          </button>
        </div>
      </motion.form>
    </motion.dialog>
  );
}

const PESTANAS_ETIQUETAS: { vista: Vista; texto: string }[] = [
  { vista: 'etiquetasPallet', texto: 'Pallets' },
  { vista: 'etiquetasUbicacion', texto: 'Ubicaciones' },
  { vista: 'manual', texto: 'Manual' },
];

/**
 * Las tres pantallas de imprimir etiquetas comparten una sola entrada del menú:
 * esta fila las une. Sólo muestra las que el rol puede ver, y con una sola no
 * hay nada que elegir.
 */
export function PestanasEtiquetas({
  actual,
  vistas,
  onVista,
}: {
  actual: Vista;
  vistas: string[];
  onVista: (v: Vista) => void;
}) {
  const visibles = PESTANAS_ETIQUETAS.filter((p) => vistas.includes(p.vista));
  if (visibles.length < 2) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
      {visibles.map((p) => (
        <ChipBoton key={p.vista} grupo="pestanas-etiquetas" activo={actual === p.vista} onClick={() => onVista(p.vista)}>
          {p.texto}
        </ChipBoton>
      ))}
    </div>
  );
}
